import { useEffect, useState } from 'react';

import {
    fetchPostFormalOffersCreditFacilityCommissionConsent,
    fetchPostFormalOffersFlexi24CommissionConsent,
    fetchGetFormalOffersCreditFacilityCommissionConsent,
    fetchGetFormalOffersFlexi24CommissionConsent,
    fetchGetFormalOffersBusinessLoanCommissionConsent,
    fetchGetFormalOffersCbilsCommissionConsent,
    fetchPostFormalOffersCbilsCommissionConsent,
    fetchGetFormalOffersIwocapayCommissionConsent,
    fetchPostFormalOffersIwocapayCommissionConsent,
    fetchGetFormalOffersRecoveryLoanCommissionConsent,
    fetchPostFormalOffersBusinessLoanCommissionConsent,
    fetchPostFormalOffersRecoveryLoanCommissionConsent,
    fetchGetFormalOffersMerchantCashAdvanceCommissionConsent,
    fetchPostFormalOffersMerchantCashAdvanceCommissionConsent
} from '@iwoca/lapi-client/edge';
import { Button, CheckBox, Label, Spinner } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import styles from './CommissionConsentModal.module.css';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { FormalOffer } from '../../../../myOffers/MyOffers.types';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { MoneyWithDecimals } from '../../../../ui/moneyWithDecimals';
import { VariableRate } from '../../../../ui/rate';

function fetchGetFormalOffersCommissionConsent({
    productType,
    ...rest
}: Parameters<typeof fetchGetFormalOffersCreditFacilityCommissionConsent>[0] & {
    productType: Exclude<FormalOffer['product_type'], undefined>;
}): ReturnType<typeof fetchGetFormalOffersCreditFacilityCommissionConsent> {
    switch (productType) {
        case 'business_loan':
            return fetchGetFormalOffersBusinessLoanCommissionConsent(rest);
        case 'cbils':
            return fetchGetFormalOffersCbilsCommissionConsent(rest);
        case 'credit_facility':
            return fetchGetFormalOffersCreditFacilityCommissionConsent(rest);
        case 'flexi24':
            return fetchGetFormalOffersFlexi24CommissionConsent(rest);
        case 'iwocapay':
        case 'iwocapay_light_assessment':
            return fetchGetFormalOffersIwocapayCommissionConsent(rest);
        case 'merchant_cash_advance':
            return fetchGetFormalOffersMerchantCashAdvanceCommissionConsent(
                rest
            );
        case 'recovery_loan':
            return fetchGetFormalOffersRecoveryLoanCommissionConsent(rest);
    }
}

function fetchPostFormalOffersCommissionConsent({
    productType,
    ...rest
}: Parameters<
    typeof fetchPostFormalOffersCreditFacilityCommissionConsent
>[0] & {
    productType: Exclude<FormalOffer['product_type'], undefined>;
}): ReturnType<typeof fetchPostFormalOffersCreditFacilityCommissionConsent> {
    switch (productType) {
        case 'business_loan':
            return fetchPostFormalOffersBusinessLoanCommissionConsent(rest);
        case 'cbils':
            return fetchPostFormalOffersCbilsCommissionConsent(rest);
        case 'credit_facility':
            return fetchPostFormalOffersCreditFacilityCommissionConsent(rest);
        case 'flexi24':
            return fetchPostFormalOffersFlexi24CommissionConsent(rest);
        case 'iwocapay':
        case 'iwocapay_light_assessment':
            return fetchPostFormalOffersIwocapayCommissionConsent(rest);
        case 'merchant_cash_advance':
            return fetchPostFormalOffersMerchantCashAdvanceCommissionConsent(
                rest
            );
        case 'recovery_loan':
            return fetchPostFormalOffersRecoveryLoanCommissionConsent(rest);
    }
}

type CommissionConsentResponse = Awaited<
    ReturnType<typeof fetchGetFormalOffersCommissionConsent>
>;

export const CommissionConsentModal = ({
    isReadOnly,
    formalOffer,
    onConfirm
}: {
    formalOffer: FormalOffer;
    isReadOnly: boolean;
    onConfirm: () => void;
}) => {
    const stateKey = useStateKey();

    const [isCheckboxChecked, setCheckboxChecked] = useState(false);

    const cashAmount = new URLSearchParams(window.location.search).get(
        'amount'
    );

    const queryClient = useQueryClient();
    const {
        data: commissionConsentDetails,
        isLoading: areCommissionsConsentDetailsLoading,
        isError: commissionConsentDetailsError
    } = useQuery({
        queryKey: [
            'commission_consent',
            stateKey,
            formalOffer.product_type,
            formalOffer.offer_id,
            cashAmount
        ],
        queryFn: async (): Promise<CommissionConsentResponse> => {
            return await fetchGetFormalOffersCommissionConsent({
                productType: formalOffer.product_type!,
                stateKey,
                offerId: formalOffer.offer_id!,
                query: {
                    cash_amount: +cashAmount!
                }
            });
        }
    });

    useEffect(() => {
        if (!commissionConsentDetails) {
            return;
        }
        setCheckboxChecked(!!commissionConsentDetails.consent_given_at);
    }, [commissionConsentDetails]);

    const submitMutation = useMutation({
        mutationFn: fetchPostFormalOffersCommissionConsent,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [
                    'commission_consent',
                    stateKey,
                    formalOffer.product_type,
                    formalOffer.offer_id,
                    cashAmount
                ]
            });
            onConfirm();
        }
    });

    function handleSubmit() {
        if (!commissionConsentDetails) {
            return;
        }
        submitMutation.mutate({
            productType: formalOffer.product_type!,
            stateKey,
            offerId: formalOffer.offer_id!,
            body: {
                commission_consent_id:
                    commissionConsentDetails.commission_consent_id,
                is_consent_given: true
            }
        });
    }

    if (areCommissionsConsentDetailsLoading) {
        return <MainContentPending />;
    }
    if (commissionConsentDetailsError) {
        return <FallbackErrorModal />;
    }

    return (
        <div className={styles.CommissionConsentModal}>
            <div className={styles.header}>
                <FormattedMessage
                    id="_.modal.commission_consent.title"
                    defaultMessage="Introducer Commission"
                >
                    {(...msg) => <h1 className={styles.title}>{msg}</h1>}
                </FormattedMessage>
                {commissionConsentDetails.commission_amount >= 0.001 && (
                    <FormattedMessage
                        tagName="div"
                        id="_.modal.commission_consent.subtitle"
                        defaultMessage="iwoca will pay {commission_amount} ({commission_rate} of {cash_amount}) to {introducer_name} as commission for this funding."
                        values={{
                            commission_amount: (
                                <MoneyWithDecimals
                                    value={
                                        commissionConsentDetails.commission_amount
                                    }
                                />
                            ),
                            commission_rate: (
                                <VariableRate
                                    minimumFractionDigits={1}
                                    maximumFractionDigits={2}
                                    value={
                                        commissionConsentDetails.commission_rate
                                    }
                                />
                            ),
                            cash_amount: (
                                <MoneyWithDecimals
                                    value={commissionConsentDetails.cash_amount}
                                />
                            ),
                            introducer_name:
                                commissionConsentDetails.introducer_name
                        }}
                    >
                        {(msg) => (
                            <p data-testid="commission-consent-message">
                                {msg}
                            </p>
                        )}
                    </FormattedMessage>
                )}
                {commissionConsentDetails.commission_type ===
                    'VOLUME_INCENTIVE' && (
                    <FormattedMessage
                        tagName="div"
                        id="_.modal.commission_consent.extra_subtitle"
                        defaultMessage="iwoca may also pay {introducer_name} an additional commission based on the total value of loans they introduce to us over time. This additional commission generally makes up no more than {extra_commission_percentage} of the total drawn amount. We set additional commissions independently of setting the interest or fees on your loan with us."
                        values={{
                            extra_commission_percentage: (
                                <VariableRate
                                    minimumFractionDigits={1}
                                    maximumFractionDigits={2}
                                    value={
                                        commissionConsentDetails.extra_commission_rate
                                    }
                                />
                            ),
                            introducer_name:
                                commissionConsentDetails.introducer_name
                        }}
                    >
                        {(msg) => (
                            <p data-testid="volume-incentive-consent-message">
                                {msg}
                            </p>
                        )}
                    </FormattedMessage>
                )}
                {commissionConsentDetails.commission_type ===
                    'REVENUE_SHARE' && (
                    <FormattedMessage
                        tagName="div"
                        id="_.modal.commission_consent.revenue_subtitle"
                        defaultMessage="When you make repayments to iwoca on this loan, we'll share up to {extra_commission_percentage} of the interest and/or fees (not loan principal) with {introducer_name} as a commission. The exact percentage will be determined by the total value of loans {introducer_name} introduces to us over time. We set commissions independently of setting the interest or fees on your loan with us."
                        values={{
                            extra_commission_percentage: (
                                <VariableRate
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                    value={
                                        commissionConsentDetails.extra_commission_rate
                                    }
                                />
                            ),
                            introducer_name:
                                commissionConsentDetails.introducer_name
                        }}
                    >
                        {(msg) => (
                            <p data-testid="revenue-share-consent-message">
                                {msg}
                            </p>
                        )}
                    </FormattedMessage>
                )}
            </div>
            <div className="flex items-center gap-s overflow-hidden">
                <CheckBox
                    id="commission-consent-checkbox"
                    onChange={(event) =>
                        setCheckboxChecked(event.currentTarget.checked)
                    }
                    checked={isCheckboxChecked}
                    disabled={isReadOnly}
                />
                <Label
                    className="cursor-pointer pt-xs font-reg"
                    htmlFor="commission-consent-checkbox"
                >
                    <FormattedMessage
                        id="_.modal.commission_consent.checkbox_label"
                        defaultMessage="I acknowledge that iwoca will pay this commission"
                    />
                </Label>
            </div>
            {!isReadOnly && (
                <Button
                    className={styles.confirmButton}
                    variant="primary"
                    disabled={
                        isReadOnly ||
                        !isCheckboxChecked ||
                        submitMutation.isLoading
                    }
                    onClick={handleSubmit}
                >
                    {submitMutation.isLoading ? (
                        <Spinner dataTestId="confirm-button-loading-indicator" />
                    ) : (
                        <FormattedMessage
                            id="_.modal.commission_consent.confirm_action"
                            defaultMessage="Confirm"
                        />
                    )}
                </Button>
            )}
        </div>
    );
};
